import React, { ReactElement, ReactNode } from 'react';
import { Segmented as AntSegmented } from 'antd';
import styled from 'styled-components';
import { colorsPalette } from '../../styles/colors-palette';

type SegmentedValueType = string | number;
type SegmentedSize = 'large' | 'small' | 'middle';
type SegmentedOption<ValueType extends SegmentedValueType> = {
  label: string | React.ReactNode;
  value: ValueType;
  disabled?: boolean;
};

interface SegmentedSwitcherCustomStyles {
  trackBackground?: string;
  trackBorderRadius?: string;
  trackPadding?: string;
  itemGap?: string;
  itemBorderRadius?: string;
  itemLabelPadding?: string;
  selectedBackground?: string;
  selectedBoxShadow?: string;
  selectedBorder?: string;
  selectedTextColor?: string;
  itemTextColor?: string;
  itemHoverBackground?: string;
}

interface SegmentedSwitcherProps<ValueType extends SegmentedValueType> {
  value?: ValueType;
  disabled?: boolean;
  size: SegmentedSize;
  fullWidth?: boolean;
  addonAfter?: () => ReactNode;
  options: Array<SegmentedOption<ValueType>>;
  onChange?: (value: ValueType | string) => void;
  className?: string;
  customStyles?: SegmentedSwitcherCustomStyles;
}

function SegmentedSwitcher<ValueType extends SegmentedValueType>({
  value,
  disabled,
  size,
  options,
  fullWidth,
  onChange,
  className,
  addonAfter,
  customStyles,
}: SegmentedSwitcherProps<ValueType>): ReactElement {
  const handleSegmentChange = (value: unknown) => {
    onChange?.(value as ValueType | string);
  };

  return (
    <SegmentedWrapper>
      <StyledSegmented
        value={value}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        options={options as any}
        size={size}
        block={fullWidth}
        disabled={disabled}
        onChange={handleSegmentChange}
        className={className}
        customstyles={customStyles}
      />
      {addonAfter && <AddonAfterWrapper>{addonAfter()}</AddonAfterWrapper>}
    </SegmentedWrapper>
  );
}

const StyledSegmented = styled(AntSegmented).withConfig({
  shouldForwardProp: prop => (prop as string) !== 'customstyles',
})<{
  customstyles?: SegmentedSwitcherCustomStyles;
}>`
  &.ant-segmented {
    color: ${props => props.customstyles?.itemTextColor ?? colorsPalette.gray700};
  }

  .ant-segmented-group {
    background: ${props => props.customstyles?.trackBackground ?? colorsPalette.lightGray300};
    border-radius: ${props => props.customstyles?.trackBorderRadius ?? '4px'};
    padding: ${props => props.customstyles?.trackPadding ?? '0'};
    gap: ${props => props.customstyles?.itemGap ?? '0'};
  }

  .ant-segmented-item {
    border-radius: ${props => props.customstyles?.itemBorderRadius ?? '4px'};
  }

  .ant-segmented-item .ant-segmented-item-label {
    padding: ${props => props.customstyles?.itemLabelPadding ?? '2px 7px'};
  }

  .ant-segmented-item-selected {
    background: ${props => props.customstyles?.selectedBackground ?? colorsPalette.white};
    ${props =>
      props.customstyles?.selectedBorder != null && props.customstyles.selectedBorder !== ''
        ? `border: ${props.customstyles.selectedBorder};`
        : ''}
    ${props =>
      props.customstyles?.selectedTextColor != null && props.customstyles.selectedTextColor !== ''
        ? `&, & .ant-segmented-item-label { color: ${props.customstyles.selectedTextColor}; }`
        : ''}
  }

  .ant-segmented-item-selected,
  .ant-segmented-thumb {
    box-shadow: ${props => props.customstyles?.selectedBoxShadow ?? 'none'};
  }

  .ant-segmented-thumb {
    background: ${props => props.customstyles?.selectedBackground ?? colorsPalette.white};
    ${props =>
      props.customstyles?.selectedBorder != null && props.customstyles.selectedBorder !== ''
        ? `border: ${props.customstyles.selectedBorder};`
        : ''}
  }

  .ant-segmented-item:not(.ant-segmented-item-selected):hover:not(:disabled) {
    color: inherit;
    background: ${props =>
      props.customstyles?.itemHoverBackground != null
        ? props.customstyles.itemHoverBackground
        : 'transparent'};
  }
`;

const SegmentedWrapper = styled.div``;

const AddonAfterWrapper = styled.div`
  position: absolute;
`;

export default SegmentedSwitcher;
export type { SegmentedSwitcherCustomStyles };
